/**
 * CFQAssessment — Thin Orchestrator
 *
 * Manages state and routes between:
 *   cfq/CFQConsentScreen.jsx  — consent before Q1
 *   cfq/CFQAssessmentForm.jsx — user details + 20 questions
 *   cfq/CFQResultsPanel.jsx   — results, PDF, opt-ins, sharing
 *
 * All scoring is local (browser/localStorage). No answers stored server-side.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CFQ_STORAGE_KEY, CFQ_SESSION_EXPIRY, domains } from './cfq/cfqData';
import { calculateScores } from './cfq/cfqScoring';
import CFQConsentScreen from './cfq/CFQConsentScreen';
import CFQAssessmentForm from './cfq/CFQAssessmentForm';
import CFQResultsPanel from './cfq/CFQResultsPanel';

const CFQAssessment = () => {
  const [searchParams] = useSearchParams();
  const sessionCode = searchParams.get('session') || '';
  const isTestMode = searchParams.get('test') === '1';

  // Always start CFQ from the top of the page — override browser scroll restoration
  // with a short delay so it fires after the browser restores its own scroll position.
  useEffect(() => {
    const t = setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 60);
    return () => clearTimeout(t);
  }, []);

  // ── Restore state from localStorage ──
  const getInitialState = useCallback(() => {
    try {
      const saved = localStorage.getItem(CFQ_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.timestamp && Date.now() - parsed.timestamp < CFQ_SESSION_EXPIRY) {
          return {
            userDetails: parsed.userDetails || { name: '', role: '', organisation: '', date: new Date().toISOString().split('T')[0] },
            responses: parsed.responses || {},
            showResults: parsed.showResults || false,
          };
        }
      }
    } catch (e) { console.error('Error loading CFQ state:', e); }
    return { userDetails: { name: '', role: '', organisation: '', date: new Date().toISOString().split('T')[0] }, responses: {}, showResults: false };
  }, []);

  const initialState = getInitialState();
  const [userDetails, setUserDetails] = useState(initialState.userDetails);
  const [responses, setResponses] = useState(initialState.responses);
  const [showResults, setShowResults] = useState(initialState.showResults);
  const [previousAttempt, setPreviousAttempt] = useState(null);
  const [consentGiven, setConsentGiven] = useState(() => {
    try { return localStorage.getItem('cfq_consent') === 'true'; } catch { return false; }
  });

  // ── Persist state ──
  useEffect(() => {
    try {
      localStorage.setItem(CFQ_STORAGE_KEY, JSON.stringify({ userDetails, responses, showResults, timestamp: Date.now() }));
    } catch {}
  }, [userDetails, responses, showResults]);

  // ── Handlers ──
  const handleResponseChange = (domainIndex, statementIndex, value) => {
    setResponses({ ...responses, [`${domainIndex}-${statementIndex}`]: parseInt(value) || 0 });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Save to CFQ history for retake comparison
    try {
      const history = JSON.parse(localStorage.getItem('cfq_history') || '[]');
      if (history.length > 0) {
        setPreviousAttempt(history[history.length - 1]);
      }
      history.push({
        date: new Date().toISOString(),
        totalScore,
        domainScores,
        userDetails: { name: userDetails.name, role: userDetails.role },
      });
      if (history.length > 5) history.splice(0, history.length - 5);
      localStorage.setItem('cfq_history', JSON.stringify(history));
    } catch {}
    setShowResults(true);
  };

  const handleRestart = () => {
    setUserDetails({ name: '', role: '', organisation: '', date: new Date().toISOString().split('T')[0] });
    setResponses({});
    setShowResults(false);
    setPreviousAttempt(null);
    try { localStorage.removeItem(CFQ_STORAGE_KEY); } catch {}
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleConsent = () => {
    setConsentGiven(true);
    try { localStorage.setItem('cfq_consent', 'true'); } catch {}
  };

  // ── Scores (computed every render, cheap) ──
  const { domainScores, totalScore } = calculateScores(responses);
  const answeredQuestions = Object.keys(responses).length;
  const totalQuestions = domains.reduce((sum, d) => sum + d.statements.length, 0);
  const progressPercent = (answeredQuestions / totalQuestions) * 100;

  // ── Screen routing ──
  if (!consentGiven) {
    return <CFQConsentScreen sessionCode={sessionCode} onConsent={handleConsent} />;
  }

  if (showResults) {
    return (
      <CFQResultsPanel
        userDetails={userDetails}
        domainScores={domainScores}
        totalScore={totalScore}
        sessionCode={sessionCode}
        isTestMode={isTestMode}
        onRestart={handleRestart}
        previousAttempt={previousAttempt}
      />
    );
  }

  return (
    <CFQAssessmentForm
      userDetails={userDetails}
      setUserDetails={setUserDetails}
      responses={responses}
      handleResponseChange={handleResponseChange}
      handleSubmit={handleSubmit}
      answeredQuestions={answeredQuestions}
      totalQuestions={totalQuestions}
      progressPercent={progressPercent}
    />
  );
};

export default CFQAssessment;
